import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { CurrentSiteState } from '@ppg/configuration';
import { LoggerService } from '@ppg/core/logger';
import { ColorInfoValid } from '@ppg/core/models';
import { ColorInfoService, ColorUtils } from '@ppg/shared/color/services';

import { CoordinatingColorsStorageService } from './coordinating-colors-storage.service';
import { CoordinatingColors, CoordinatingColorsStorageModel } from '../models/coordinating-colors.model';

@Injectable({ providedIn: 'root' })
export class CoordinatingColorsService {
  readonly #storageService = inject(CoordinatingColorsStorageService);
  readonly #currentSiteState = inject(CurrentSiteState);
  readonly #logger = inject(LoggerService);
  readonly #colorInfoService = inject(ColorInfoService);
  readonly #colorUtils = inject(ColorUtils);

  getCoordinatingColors(colorInfo: ColorInfoValid): Observable<CoordinatingColors[]> {
    const currentSite = this.#currentSiteState.currentSite();

    if (!currentSite) {
      this.#logger.warn(
        `[CoordinatingColorsService] Current site is not available while trying to run Coordinating Colors algorithm`,
      );

      return of([]);
    }

    return this.#storageService.getAllCoordinatingColors(currentSite.id).pipe(
      switchMap((coordinatingColorsData) => {
        const coordinatingColors: CoordinatingColorsStorageModel[] | undefined =
          coordinatingColorsData[colorInfo.productNumber];

        if (coordinatingColors) {
          return this.#loadAndMapColors(coordinatingColors);
        }

        this.#logger.warn(
          `[CoordinatingColorsService] Coordinating colors for color ${colorInfo.productNumber} not found in the storage`,
        );

        return of([]);
      }),
      catchError((error) => {
        this.#logger.warn(
          `[CoordinatingColorsService] Error while trying to get all Coordinating Colors from storage: ${error}`,
        );

        return of([]);
      }),
    );
  }

  #loadAndMapColors(coordinatingColors: CoordinatingColorsStorageModel[]): Observable<CoordinatingColors[]> {
    const colorsToLoad = coordinatingColors.map((cc) => [cc.sec, cc.acc]).flat();

    return this.#colorInfoService
      .getColorInfosWithFamilies(colorsToLoad)
      .pipe(map((loadedColors) => this.#mapColors(coordinatingColors, loadedColors)));
  }

  #mapColors(storageColors: CoordinatingColorsStorageModel[], loadedColors: ColorInfoValid[]): CoordinatingColors[] {
    return storageColors.map((cc) => {
      const colorsInfos = this.#colorUtils.matchColorsToProductNumbers(loadedColors, [cc.sec, cc.acc]);

      return {
        algorithm: cc.alg,
        secondary: colorsInfos[0],
        accent: colorsInfos[1],
      };
    });
  }
}
